<template>
	<Row :gutter="[16, 8]">
		<Col :span="18" :offset="0">
			<VerticalLayout>
				<StatsChartTitleWithDescription
					titleKey="stats.residuals.residualsByBaseStationChart"
					descriptionKey="stats.residuals.residualsByBaseStationChartTooltip"
					titleClass="pd-lg-x pd-md-top"
				/>
				<TimeSeriesChart v-if="mountpointsSelected.length > 0" :isLoading="isLoading" :series="series" :options="chartOptions" :height="500" />
				<EmptyChartFiltersMessage v-else messageKey="stats.residuals.baseStationsPlaceholder" />
			</VerticalLayout>
		</Col>
		<Col :span="6" :offset="0">
			<VerticalLayout>
				<Card :bordered="false">
					<VerticalLayout>
						<BaseStationSelect v-model="mountpointsSelected" :onUpdate="fetchStatsItems" subNetworkFilter />
						<GnssSelect ref="gnssSelectRef" :onChange="fetchStatsItems" />
						<DateRangeChooser v-model="datesRange" @change="fetchStatsItems" />
						<VerticalLayout v-if="isAggregated" class="pd-vertical">
							<LabeledSwitch v-model="showMin" :rightLabel="$t('controls.switches.showMin')" />
							<LabeledSwitch v-model="showMedian" :rightLabel="$t('controls.switches.showMedian')" />
							<LabeledSwitch v-model="showMax" :rightLabel="$t('controls.switches.showMax')" />
						</VerticalLayout>
					</VerticalLayout>
				</Card>
			</VerticalLayout>
		</Col>
	</Row>
</template>

<script>
import { ref, computed, watch } from 'vue';
import { Row, Col, Card } from 'ant-design-vue';

import Api from '@/api/Api';

import { useSessionStore } from '@/stores/session';
import { useSubNetworksStore } from '@/stores/subNetworksStore';
import { useDateRangeAdapter } from '@/utils/useDateRangeAdapter';

import { getBaseOptions, generateSeries, setXAxisRange, nullSafeFormatter } from '@/utils/chartsUtils';

import MapHelpers from '@/components/map/utils/MapHelpers';
import VerticalLayout from '@/components/common/VerticalLayout';
import EmptyChartFiltersMessage from '@/components/charts/EmptyChartFiltersMessage';

import DateRangeChooser from '@/components/common/DateRangeChooser';
import TimeSeriesChart from '@/components/stats/TimeSeriesChart';
import StatsChartTitleWithDescription from '@/components/stats/StatsChartTitleWithDescription.vue';
import BaseStationSelect from '@/components/common/controls/BaseStationSelect';
import GnssSelect from '@/components/common/controls/GnssSelect.vue';
import LabeledSwitch from '@/components/common/LabeledSwitch.vue';


const getMountpointSeriesKey = (period) => item => {
	const gnssName = MapHelpers.getGnssDataDisplay(item.client).name;
	if (period > 1 && item.agg_function) {
		return item.mountpoint + '-' + gnssName + ' ' + item.agg_function;
	}
	return item.mountpoint + '-' + gnssName;
};
const getValue = item => item.final_value / 1_000_000;

export default {
	components: {
		Row, Col, Card,
		VerticalLayout, GnssSelect, DateRangeChooser, TimeSeriesChart, EmptyChartFiltersMessage,
		BaseStationSelect, LabeledSwitch, StatsChartTitleWithDescription,
	},
	setup() {
		const { apiData, isLoading, doRequest } = Api.useResidualsByBaseStationStatsAggregatedGet();
		const sessionStore = useSessionStore();
		const { datesRange, datesRangeStr, datesRangeSafe } = useDateRangeAdapter(sessionStore, 1, 0);
		const gnssSelectRef = ref();
		const mountpointsSelected = ref([]);
		const showMin = ref(true);
		const showMedian = ref(true);
		const showMax = ref(true);
		const isAggregated = computed(() => apiData.value.period > 1);
		const subNetworksStore = useSubNetworksStore();
		const fetchStatsItems = () => {
			if (datesRangeStr.value != null && mountpointsSelected.value.length > 0) {
				const gnssSelectedValue = gnssSelectRef.value != null ? gnssSelectRef.value.selected : [];
				doRequest({
					start_date: datesRangeStr.value[0],
					end_date: datesRangeStr.value[1],
					mountpoints: mountpointsSelected.value,
					sub_network_id: subNetworksStore.subNetworkSelectedId,
					gnss: gnssSelectedValue,
					show_min: showMin.value,
					show_median: showMedian.value,
					show_max: showMax.value,
				});
			}
		};
		watch(() => subNetworksStore.subNetworkSelectedId, () => {
			fetchStatsItems();
		});
		watch([showMin, showMedian, showMax], () => {
			fetchStatsItems();
		});

		const series = computed(() => generateSeries({
			items: apiData.value.statsItems.filter(item => item.kind === 'residuals'),
			getSeriesKeyFn: getMountpointSeriesKey(apiData.value.period),
			getValueFn: getValue,
			seriesStep: apiData.value.period,
		}).series);

		const chartOptions = computed(() => {
			let options = getBaseOptions('residualsAggregated', series.value, apiData.value.funcCount);
			options.yaxis.title.text = 'metres';
			options.yaxis.decimalsInFloat = 6;
			setXAxisRange(options, datesRangeSafe.value);
			options.tooltip.y.formatter = nullSafeFormatter(value => value.toFixed(6) + ' m');
			return options;
		});

		return {
			isLoading,
			datesRange,
			mountpointsSelected,
			gnssSelectRef,
			showMin,
			showMedian,
			showMax,
			isAggregated,
			series,
			chartOptions,
			fetchStatsItems,
		};
	},
}
</script>
